import { zodResolver } from "@hookform/resolvers/zod";
import { cn, KdsButton } from "@kazaar-platform/design-system";
import { RiDeleteBinLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { OrderGetOrderDetailsOrderItemReadDtoOrderItemIssuesEnum } from "@/config/apiClient";
import { getOriginalOrderItem } from "@/helpers/getOriginalOrderItem";
import { transformFormDataToUpdateCommand } from "@/helpers/transformFormDataToUpdateCommand";
import { useOrderItems } from "@/hooks";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useFocusTracking } from "@/hooks/useFocusTracking";
import { usePreserveFormValues } from "@/hooks/usePreserveFormValues";
import { BriefSection } from "./BriefSection";
import { DeliverySection } from "./DeliverySection";
import { ModalPresetAttributes } from "./ModalPresetAttributes";
import {
  getDefaultValues,
  type OrderItemConfigurationSchema,
  orderItemConfigurationSchema,
} from "./OrderItemConfigForm.schema";
import { type OrderItemConfigFormProps } from "./OrderItemConfigForm.types";
import { ProductSection } from "./ProductSection";
import { ReferenceIdSection } from "./ReferenceIdSection";

export const OrderItemConfigForm = ({
  orderItem,
  order,
  showPresetAttributesModal,
  onRemoveItem,
  hasAutoSave,
  isModificationRequest,
  methods: methodsProps,
  className,
}: OrderItemConfigFormProps) => {
  const { t } = useTranslate();

  const classNames = cn("flex flex-col", className);

  const [isBriefSectionOpen, setIsBriefSectionOpen] = useState(true);
  const [isDeliverySectionOpen, setIsDeliverySectionOpen] = useState(true);
  const [isReferenceIdSectionOpen, setIsReferenceIdSectionOpen] =
    useState(true);
  const [isPresetAttributesModalOpen, setIsPresetAttributesModalOpen] =
    useState(!!showPresetAttributesModal);

  const { useUpdateOrderItem } = useOrderItems();
  const { mutateAsync: updateOrderItem } = useUpdateOrderItem();

  const fallbackMethods = useForm<OrderItemConfigurationSchema>({
    criteriaMode: "all",
    mode: "all",
    resolver: zodResolver(orderItemConfigurationSchema),
    defaultValues: getDefaultValues({ orderItem }),
  });

  const methods = methodsProps ?? fallbackMethods;
  const isControlled = !!methodsProps;

  // Track focused fields to preserve their values during form resets
  const focusedFieldRef = useFocusTracking(fallbackMethods);

  // Preserve form values (focused fields, uploaded files) when orderItem changes
  usePreserveFormValues(orderItem, fallbackMethods, focusedFieldRef);

  const originalOrderItem = getOriginalOrderItem(order, orderItem.id);

  const isCatalogMediumDeleted = useMemo(
    () =>
      orderItem.orderItemIssues?.includes(
        OrderGetOrderDetailsOrderItemReadDtoOrderItemIssuesEnum.CatalogMediumDeleted
      ),
    [orderItem]
  );

  const handleSubmit = async (data: OrderItemConfigurationSchema) => {
    if (isCatalogMediumDeleted) {
      return null;
    }

    const orderItemUpdateCommand = transformFormDataToUpdateCommand(data);

    await updateOrderItem({
      limitedRefetch: true,
      orderId: orderItem.orderId,
      orderItemId: orderItem.id,
      orderItemUpdateCommand,
    });

    methods.reset(methods.getValues());
  };

  useAutoSave({
    methods,
    isEnabled: !!hasAutoSave,
    onSave: () => void handleSubmit(methods.getValues()),
  });

  // TODO: May be extract this as a generic component to be used elsewhere
  const ConditionalWrapper = useCallback(
    ({ children }: { children: ReactNode }) => {
      if (isControlled) {
        return children;
      }
      return <FormProvider {...methods}>{children}</FormProvider>;
    },
    [methods, isControlled]
  );

  return (
    <ConditionalWrapper>
      <div className={classNames}>
        {/** Product and delivery date */}
        <ProductSection
          isCatalogMediumDeleted={isCatalogMediumDeleted}
          sandboxItem={orderItem.sandboxItem}
          onScrollToBrief={() => setIsBriefSectionOpen(true)}
        />

        {!isCatalogMediumDeleted && (
          <>
            {/** Version and dispatch */}
            <div className="py-4">
              <DeliverySection
                isOpen={isDeliverySectionOpen}
                isModificationRequest={isModificationRequest}
                onOpenChange={() =>
                  setIsDeliverySectionOpen(!isDeliverySectionOpen)
                }
              />
            </div>

            {/** Brief and PreMedia */}
            <div className="py-4" id="brief">
              <BriefSection
                isOpen={isBriefSectionOpen}
                onOpenChange={() => setIsBriefSectionOpen(!isBriefSectionOpen)}
                orderItem={orderItem}
                isModificationRequest={isModificationRequest ?? false}
              />
            </div>

            {/** Reference ID Field */}
            <div className="py-sp-base">
              <ReferenceIdSection
                isOpen={isReferenceIdSectionOpen}
                onOpenChange={() =>
                  setIsReferenceIdSectionOpen(!isReferenceIdSectionOpen)
                }
              />
            </div>
          </>
        )}

        {/** Delete product from order */}
        {!!onRemoveItem && (
          <div className="flex justify-center">
            <KdsButton
              variant="ghost"
              color="error"
              size="medium"
              iconLeft={RiDeleteBinLine}
              onPress={onRemoveItem}
            >
              {t("orderItemConfig.deleteProduct")}
            </KdsButton>
          </div>
        )}

        {!!isPresetAttributesModalOpen && originalOrderItem && (
          <ModalPresetAttributes
            isOpen
            originalOrderItem={originalOrderItem}
            onOpenChange={() => setIsPresetAttributesModalOpen(false)}
          />
        )}
      </div>
    </ConditionalWrapper>
  );
};
